
import React, { useState, useEffect } from 'react';
import { Page } from '../types';

type ConsentSettings = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
};

interface CookieBannerProps {
  navigate?: (page: Page) => void;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ navigate }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<ConsentSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
    timestamp: new Date().toISOString()
  });

  useEffect(() => {
    const consent = localStorage.getItem('eh2go-consent');
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (updatedSettings: ConsentSettings) => {
    localStorage.setItem('eh2go-consent', JSON.stringify(updatedSettings));
    setIsVisible(false);
  };

  const acceptAll = () => {
    const allIn = { 
      necessary: true, 
      analytics: true, 
      marketing: true,
      timestamp: new Date().toISOString()
    };
    saveConsent(allIn);
  };

  const acceptNecessary = () => {
    const min = { 
      necessary: true, 
      analytics: false, 
      marketing: false,
      timestamp: new Date().toISOString()
    };
    saveConsent(min);
  };

  const saveSelection = () => {
    saveConsent({
      ...settings,
      timestamp: new Date().toISOString()
    });
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 z-[100] md:max-w-xl md:left-auto md:right-8 animate-fade-in"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-title"
    >
      <div className="bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.2)] p-6 md:p-8 border border-gray-100 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12" aria-hidden="true"></div>
        
        {!showSettings ? (
          <div className="flex flex-col gap-6 relative z-10">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shadow-inner" aria-hidden="true">🍪</div>
              <div>
                <h4 id="cookie-title" className="text-xl font-black text-secondary">Cookie-Einstellungen</h4>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Sicherheit & Transparenz</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 leading-relaxed">
              Wir verwenden Cookies, um die Kernfunktionen unserer Website zu ermöglichen und dein Erlebnis zu verbessern. Weitere Details findest du in unserer <button onClick={() => navigate?.('datenschutz')} className="text-primary font-bold underline hover:text-secondary transition-colors focus:ring-2 focus:ring-primary rounded px-1">Datenschutzerklärung</button>.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                onClick={acceptAll}
                className="flex-1 bg-primary text-white px-6 py-4 rounded-2xl font-black hover:scale-[1.02] transition-all shadow-lg shadow-primary/20 text-sm active:scale-95 focus:ring-4 focus:ring-primary/30"
              >
                Alle akzeptieren
              </button>
              <button 
                onClick={acceptNecessary}
                className="flex-1 bg-gray-100 text-secondary px-6 py-4 rounded-2xl font-bold hover:bg-gray-200 transition-all text-sm active:scale-95 focus:ring-4 focus:ring-gray-200"
              >
                Nur notwendige
              </button>
            </div>
            
            <button 
              onClick={() => setShowSettings(true)}
              className="text-center text-xs text-gray-400 font-bold hover:text-secondary transition-all underline underline-offset-4 focus:ring-2 focus:ring-gray-200 rounded py-1"
            >
              Individuelle Einstellungen
            </button>
          </div>
        ) : (
          <div className="space-y-6 relative z-10" role="group" aria-labelledby="settings-title">
            <div className="flex items-center justify-between mb-4">
               <h4 id="settings-title" className="text-xl font-black text-secondary">Einstellungen anpassen</h4>
               <button 
                onClick={() => setShowSettings(false)} 
                className="text-gray-400 hover:text-secondary p-2 focus:ring-2 focus:ring-gray-100 rounded-full"
                aria-label="Einstellungen schließen"
               >
                ✕
               </button>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-5 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="pr-4">
                  <p className="font-bold text-secondary text-sm">Essenziell</p>
                  <p className="text-[10px] text-gray-500 leading-tight">Notwendig für den Betrieb der Seite (Login, Sicherheit, Warteliste).</p>
                </div>
                <div className="w-12 h-6 bg-primary/20 rounded-full relative cursor-not-allowed" aria-hidden="true">
                  <div className="absolute right-1 top-1 w-4 h-4 bg-primary rounded-full shadow-sm"></div>
                </div>
                <span className="sr-only">Immer aktiv (Essenziell)</span>
              </div>

              <div className="flex items-center justify-between p-5 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="pr-4">
                  <p id="label-analytics" className="font-bold text-secondary text-sm">Analyse & Performance</p>
                  <p className="text-[10px] text-gray-500 leading-tight">Hilft uns zu verstehen, wie die App genutzt wird, um sie zu optimieren.</p>
                </div>
                <button 
                  onClick={() => setSettings({...settings, analytics: !settings.analytics})}
                  className={`w-12 h-6 rounded-full relative transition-all shadow-inner focus:ring-2 focus:ring-primary ${settings.analytics ? 'bg-primary' : 'bg-gray-300'}`}
                  role="switch"
                  aria-checked={settings.analytics}
                  aria-labelledby="label-analytics"
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${settings.analytics ? 'right-1' : 'left-1'}`}></div>
                </button>
              </div>

              <div className="flex items-center justify-between p-5 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="pr-4">
                  <p id="label-marketing" className="font-bold text-secondary text-sm">Marketing</p>
                  <p className="text-[10px] text-gray-500 leading-tight">Ermöglicht uns, relevante Informationen über neue Kurse zu teilen.</p>
                </div>
                <button 
                  onClick={() => setSettings({...settings, marketing: !settings.marketing})}
                  className={`w-12 h-6 rounded-full relative transition-all shadow-inner focus:ring-2 focus:ring-primary ${settings.marketing ? 'bg-primary' : 'bg-gray-300'}`}
                  role="switch"
                  aria-checked={settings.marketing}
                  aria-labelledby="label-marketing"
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${settings.marketing ? 'right-1' : 'left-1'}`}></div>
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button 
                onClick={saveSelection}
                className="flex-1 bg-secondary text-white px-6 py-4 rounded-2xl font-black hover:bg-secondary/95 transition-all text-sm active:scale-95 focus:ring-4 focus:ring-secondary/30"
              >
                Auswahl speichern
              </button>
              <button 
                onClick={acceptAll}
                className="flex-1 bg-primary text-white px-6 py-4 rounded-2xl font-black hover:opacity-90 transition-all text-sm active:scale-95 focus:ring-4 focus:ring-primary/30"
              >
                Alle akzeptieren
              </button>
            </div>
            
            <p className="text-[9px] text-gray-400 text-center italic" aria-hidden="true">
              Deine Einstellungen werden für 12 Monate gespeichert. Du kannst sie jederzeit im Impressum ändern.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
